import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faChevronRight } from "@fortawesome/free-solid-svg-icons";

import HollowButton from "components/HollowButton";
import { Option } from "components/OptionsCard/typings";
import OutgoingPrintModal from "components/OutgoingPrintModal";
import ReceiverDataOutdatedBadge from "components/ReceiverDataOutdatedBadge";
import RefreshReceiverDataModal from "components/RefreshReceiverDataModal";
import officialLettersService from "api/officialLettersService";
import { EParcelDispatchStage, IDispatchListItem } from "dto/Mailroom/IDispatchChart";
import { IOutgoingLetter, IOutgoingLetterParcel } from "dto/OfficialLetters/IOfficialLetters";
import { IOutgoingPrintSource } from "dto/Printing/IOutgoingPrint";
import ChangeDeliveryKindModal from "modules/biuroPodawczeModule/ChangeDeliveryKindModal";
import ParcelPreviewModal from "modules/biuroPodawczeModule/ParcelPreviewModal";
import { ENotificationType, notification } from "utils/notification";

import LetterRowMenu from "./LetterRowMenu";
import useLetterActions from "./useLetterActions";
import styles from "./officialLetters.module.scss";

interface Props {
  searchText: string;
}

const fmtDate = (v?: string | null) => (v ? new Date(v).toLocaleDateString("pl-PL") : "—");

const stageClass = (p: IOutgoingLetterParcel) => {
  if (p.HasSendError) return styles.badgeError;
  switch (p.Stage) {
    case EParcelDispatchStage.Sent:
    case EParcelDispatchStage.Received:
      return styles.badgeOk;
    case EParcelDispatchStage.Returned:
      return styles.badgeError;
    case EParcelDispatchStage.InChart:
      return styles.badgeInfo;
    default:
      return styles.badgeWarn;
  }
};

const matchesSearch = (l: IOutgoingLetter, q: string) => {
  const has = (v?: string | null) => !!v && v.toLowerCase().includes(q);
  return (
    has(l.Title) ||
    has(l.CaseSignature) ||
    has(l.Receivers) ||
    has(l.MajorSignature) ||
    fmtDate(l.Date).includes(q) ||
    l.Parcels.some((p) => has(p.ParcelNumber) || has(p.DeliveryKindName) || has(p.ReceiverName))
  );
};

// „Wychodzące pisma" — port Documents.List.OutgoingDocumentsList z Magistratu. Pismo rozwija
// się w wygenerowane przesyłki z etapem nadania; znika z listy, gdy wszystkie przesyłki
// zostaną wysłane. Samo nadanie (karty nadawcze, koperty, zwrotki) odbywa się w biurze podawczym.
const OutgoingLettersList = ({ searchText }: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data, isLoading } = officialLettersService.useGetOutgoing();
  const { mutate: returnToProject } = officialLettersService.useReturnToProject();
  const { invalidate, run, confirmModal } = useLetterActions();

  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [preview, setPreview] = useState<{ parcelId: string; title?: string | null } | null>(null);
  const [changeKind, setChangeKind] = useState<IDispatchListItem[] | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [printing, setPrinting] = useState<{ source: IOutgoingPrintSource; title: string } | null>(null);
  // „Aktualizuj dane adresata” — przesyłki do przepięcia na bieżące dane z książki adresowej.
  const [refreshIds, setRefreshIds] = useState<string[] | null>(null);

  const q = searchText.trim().toLowerCase();
  const items = useMemo(
    () => (data?.data?.Items ?? []).filter((l) => q.length === 0 || matchesSearch(l, q)),
    [data, q],
  );

  useEffect(() => setSelected(new Set()), [q]);

  const selectedItems = items.filter((l) => selected.has(l.DocumentId));

  const toggleSelected = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  // Magistrat: „Drukuj" w wierszu (kolejka adresatów pisma) i „Drukuj" zaznaczonych pism.
  const printLetters = (letters: IOutgoingLetter[]) =>
    setPrinting({
      source: { DocumentIds: letters.map((l) => l.DocumentId) },
      title: letters.length === 1 ? letters[0].Title || "Pismo bez tytułu" : `Zaznaczone pisma (${letters.length})`,
    });

  const toggle = (id: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const canChangeKind = (p: IOutgoingLetterParcel) =>
    !p.IsSent && !p.DispatchChartId && !p.IsGrouped && p.Stage === EParcelDispatchStage.ReadyToDispatch;

  const toDispatchItem = (l: IOutgoingLetter, p: IOutgoingLetterParcel): IDispatchListItem =>
    ({
      Id: p.Id,
      ParcelNumber: p.ParcelNumber ?? null,
      Title: l.Title ?? null,
      ReceiverName: p.ReceiverName ?? null,
      ReceiverAddress: p.ReceiverAddress ?? null,
      DeliveryKindId: p.DeliveryKindId ?? null,
      DeliveryKindName: p.DeliveryKindName ?? null,
      EditorDocumentId: l.DocumentId,
    }) as IDispatchListItem;

  // „Popraw" (Magistrat: ReturnCommand) — jedyna droga do edycji pisma z wygenerowanymi
  // przesyłkami: usuwa przesyłki i cofa pismo do „Projektów pism". Zablokowane, gdy którakolwiek
  // przesyłka jest na karcie nadawczej albo wysłana — wtedy komunikat, jak w Magistracie.
  const returnLetter = (l: IOutgoingLetter) => {
    if (!l.CanReturnToProject) {
      notification({
        type: ENotificationType.INFO,
        title: "Nie można poprawić pisma",
        text: "Co najmniej jedna z przesyłek tego pisma została już dodana do karty nadawczej lub wysłana.",
      });
      return;
    }
    run(
      returnToProject,
      [l.DocumentId],
      "Cofnięto do projektów",
      {
        title: "Popraw pismo",
        text: "Wygenerowane przesyłki tego pisma zostaną usunięte, a pismo wróci do „Projektów pism”, gdzie można je poprawić i wygenerować ponownie. Kontynuować?",
        acceptLabel: "Popraw",
      },
    );
  };

  const letterOptions = (l: IOutgoingLetter): Option[] => [
    {
      name: "Drukuj…",
      callback: () => printLetters([l]),
      additional: false,
      isEnabled: l.Parcels.length > 0,
      canMultiple: false,
    },
    {
      name: "Zmień sposób nadania",
      callback: () => setChangeKind(l.Parcels.filter(canChangeKind).map((p) => toDispatchItem(l, p))),
      additional: false,
      isEnabled: l.Parcels.some(canChangeKind),
      canMultiple: false,
    },
    {
      name: t("receiverData.refresh"),
      callback: () => setRefreshIds(l.Parcels.filter((p) => p.ReceiverDataOutdated).map((p) => p.Id)),
      additional: false,
      isEnabled: l.Parcels.some((p) => p.ReceiverDataOutdated),
      canMultiple: false,
    },
    { name: "Biuro podawcze — wysyłka", callback: () => navigate("/biuro-podawcze/outgoing"), additional: false, isEnabled: true, canMultiple: false },
    {
      name: "Popraw",
      callback: () => returnLetter(l),
      additional: false,
      isEnabled: true,
      canMultiple: false,
      splitterBefore: true,
    },
    {
      name: "Idź do teczki",
      callback: () => navigate(`/cases/${l.CaseId}/dossier`),
      additional: false,
      isEnabled: !!l.CaseId,
      canMultiple: false,
      splitterBefore: true,
    },
  ];

  const parcelOptions = (l: IOutgoingLetter, p: IOutgoingLetterParcel): Option[] => [
    {
      name: "Drukuj…",
      callback: () => setPrinting({ source: { ParcelIds: [p.Id] }, title: `${l.Title ?? ""} — ${p.ReceiverName ?? ""}` }),
      additional: false,
      isEnabled: true,
      canMultiple: false,
    },
    {
      name: "Podgląd pisma",
      callback: () => setPreview({ parcelId: p.Id, title: `${l.Title ?? ""} — ${p.ReceiverName ?? ""}` }),
      additional: false,
      isEnabled: true,
      canMultiple: false,
    },
    {
      name: "Zmień sposób nadania",
      callback: () => setChangeKind([toDispatchItem(l, p)]),
      additional: false,
      isEnabled: canChangeKind(p),
      canMultiple: false,
    },
    {
      name: t("receiverData.refresh"),
      callback: () => setRefreshIds([p.Id]),
      additional: false,
      isEnabled: !!p.ReceiverDataOutdated,
      canMultiple: false,
    },
  ];

  const renderParcel = (l: IOutgoingLetter, p: IOutgoingLetterParcel) => (
    <div
      key={p.Id}
      className={styles.parcel}
      title="Dwuklik — podgląd pisma"
      onDoubleClick={() => setPreview({ parcelId: p.Id, title: `${l.Title ?? ""} — ${p.ReceiverName ?? ""}` })}
    >
      <div className={styles.parcelMain}>
        <div className={styles.parcelTitle}>
          {p.ReceiverName ?? "—"}
          {p.ParcelNumber && <span className={styles.sig}>{p.ParcelNumber}</span>}
          <span className={`${styles.badge} ${stageClass(p)}`}>
            {p.HasSendError ? "Błąd wysyłki" : p.StageLabel}
          </span>
          {p.DispatchChartSignature && <span className={`${styles.badge} ${styles.badgeGray}`}>karta {p.DispatchChartSignature}</span>}
          {p.IsGrouped && <span className={`${styles.badge} ${styles.badgeGray}`}>koperta wspólna</span>}
          {p.ReceiverDataOutdated && <ReceiverDataOutdatedBadge />}
        </div>
        <div className={styles.parcelSub}>
          {p.DeliveryKindName ?? "brak rodzaju przesyłki"}
          {p.ReceiverAddress ? ` · ${p.ReceiverAddress}` : ""}
        </div>
      </div>
      <div className={styles.parcelMeta}>{p.IsSent ? `wysłane ${fmtDate(p.SendDate)}` : "do wysłania"}</div>
      <LetterRowMenu options={parcelOptions(l, p)} />
    </div>
  );

  const renderLetter = (l: IOutgoingLetter) => {
    const open = !collapsed.has(l.DocumentId);
    return (
      <div key={l.DocumentId} className={styles.card}>
        <div className={styles.cardHead} onClick={() => toggle(l.DocumentId)}>
          <input
            type="checkbox"
            checked={selected.has(l.DocumentId)}
            onChange={() => toggleSelected(l.DocumentId)}
            onClick={(e) => e.stopPropagation()}
          />
          <button type="button" className={styles.expandBtn} title={open ? "Zwiń" : "Rozwiń"}>
            <FontAwesomeIcon icon={open ? faChevronDown : faChevronRight} />
          </button>
          <div className={styles.cardMain}>
            <div className={styles.cardTitle}>
              {l.Title || "Bez tytułu"}
              {l.MajorSignature && <span className={styles.sig}>{l.MajorSignature}</span>}
            </div>
            <div className={styles.cardSub}>
              {l.Receivers ?? ""}
              {l.CaseSignature ? ` · teczka ${l.CaseSignature}` : ""}
            </div>
          </div>
          <div className={styles.cardMeta}>
            <div className={styles.progress}>
              {l.SentCount}/{l.TotalCount} wysłanych
            </div>
            <div>{fmtDate(l.Date)}</div>
          </div>
          <div onClick={(e) => e.stopPropagation()}>
            <LetterRowMenu options={letterOptions(l)} />
          </div>
        </div>
        {open && <div className={styles.parcels}>{l.Parcels.map((p) => renderParcel(l, p))}</div>}
      </div>
    );
  };

  const total = data?.data?.Items?.length ?? 0;
  const pending = items.reduce((sum, l) => sum + (l.TotalCount - l.SentCount), 0);

  return (
    <>
      <div className={styles.topBar}>
        <div className={styles.topInfo}>
          {selected.size > 0 ? (
            `Zaznaczono: ${selected.size}`
          ) : (
            <>
              Pism: {items.length}
              {items.length !== total ? ` z ${total}` : ""} · przesyłek do wysłania: {pending}
            </>
          )}
        </div>
        {selectedItems.length > 0 && (
          <div className={styles.topActions}>
            <HollowButton text={`Drukuj (${selectedItems.length})`} onClick={() => printLetters(selectedItems)} />
          </div>
        )}
      </div>

      <div className={styles.content}>
        {isLoading ? (
          <div className={styles.empty}>Ładowanie…</div>
        ) : items.length === 0 ? (
          <div className={styles.empty}>
            {total === 0
              ? "Brak pism w wysyłce. Pismo pojawia się tu po przekazaniu do wysyłki i znika, gdy wszystkie jego przesyłki zostaną wysłane."
              : "Brak pism spełniających kryteria."}
          </div>
        ) : (
          <div className={styles.cards}>{items.map(renderLetter)}</div>
        )}
      </div>

      {preview && <ParcelPreviewModal parcelId={preview.parcelId} title={preview.title} onClose={() => setPreview(null)} />}
      {printing && (
        <OutgoingPrintModal
          source={printing.source}
          context="outgoingLetters"
          title={printing.title}
          onClose={() => setPrinting(null)}
        />
      )}
      {changeKind && changeKind.length > 0 && (
        <ChangeDeliveryKindModal
          items={changeKind}
          onClose={() => setChangeKind(null)}
          onDone={() => {
            setChangeKind(null);
            invalidate();
          }}
        />
      )}
      {refreshIds && (
        <RefreshReceiverDataModal parcelIds={refreshIds} onClose={() => setRefreshIds(null)} onDone={invalidate} />
      )}
      {confirmModal}
    </>
  );
};

export default OutgoingLettersList;
